import React from "react";
import { Printer } from "lucide-react";

const GOLD = "#c9a227";
const INK = "#0a0a0a";

/**
 * /plan — Tatva K's private execution playbook page.
 * PURE ONLINE STACK ONLY — no in-person, no corporate.
 * Print-optimized. Open in browser → Cmd+P → Save as PDF.
 */
const Plan = () => {
    const handlePrint = () => window.print();

    return (
        <main
            data-testid="plan-page"
            className="plan-page"
            style={{
                backgroundColor: INK,
                color: "#e8e8e8",
                fontFamily: "Inter, sans-serif",
                minHeight: "100vh",
                padding: "60px 32px 100px",
                lineHeight: 1.6,
            }}
        >
            <div
                className="plan-screen-only"
                style={{
                    maxWidth: 880,
                    margin: "0 auto 32px",
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <button
                    onClick={handlePrint}
                    data-testid="plan-print-btn"
                    style={{
                        backgroundColor: GOLD,
                        color: INK,
                        border: "none",
                        padding: "12px 22px",
                        fontSize: 12,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <Printer size={14} /> Save / Print PDF
                </button>
            </div>

            <article
                style={{
                    maxWidth: 880,
                    margin: "0 auto",
                    backgroundColor: INK,
                    border: `1px solid rgba(201,162,39,0.18)`,
                    padding: "64px 56px",
                }}
            >
                <header style={{ marginBottom: 56, textAlign: "center" }}>
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 10.5,
                            letterSpacing: "0.42em",
                            textTransform: "uppercase",
                            margin: "0 0 18px",
                            fontWeight: 600,
                        }}
                    >
                        The Reset Sanctuary
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: "clamp(32px, 4vw, 48px)",
                            fontWeight: 400,
                            lineHeight: 1.1,
                            letterSpacing: "-0.02em",
                            margin: "0 0 16px",
                        }}
                    >
                        Tatva K&apos;s{" "}
                        <span style={{ color: GOLD }}>
                            12-Month Playbook
                        </span>
                    </h1>
                    <p
                        style={{
                            color: "#fff",
                            fontSize: 16,
                            margin: "0 0 6px",
                            letterSpacing: "0.02em",
                            fontStyle: "italic",
                            fontWeight: 400,
                        }}
                    >
                        AED 30-40K/mo from Insight Timer alone by Month 3.
                        AED 180-250K/mo combined by Month 12.
                    </p>
                    <p
                        style={{
                            color: "#777",
                            fontSize: 12,
                            margin: 0,
                            letterSpacing: "0.02em",
                        }}
                    >
                        Pure online · Pure scalable · Veer&apos;s vision,
                        operationalised.
                    </p>
                </header>

                <section style={blockBox()}>
                    <p style={quoteStyle()}>
                        &ldquo;Online is the most important. Focus on online
                        sessions and making monies online so you can scale
                        it indefinitely around the globe and DXB and make
                        lots of monies and have time for us too to have
                        fun.&rdquo;
                    </p>
                    <p style={attribStyle()}>— Veer</p>
                </section>

                <H2>The Year At A Glance</H2>
                <Table
                    headers={[
                        "Phase",
                        "Months",
                        "IT Alone (AED)",
                        "+ Bonus",
                        "Combined (AED)",
                        "Confidence",
                    ]}
                    rows={[
                        [
                            "Foundation",
                            "1-3",
                            "30-40K",
                            "+10-18K",
                            "40-58K",
                            "94%",
                        ],
                        [
                            "Compound",
                            "4-6",
                            "45-60K",
                            "+15-25K",
                            "60-85K",
                            "92%",
                        ],
                        [
                            "Scale",
                            "7-9",
                            "60-85K",
                            "+30-45K",
                            "90-130K",
                            "85%",
                        ],
                        [
                            "Empire",
                            "10-12",
                            "80-110K",
                            "+50-80K",
                            "130-190K",
                            "82%",
                        ],
                        [
                            "Year-End State",
                            "Month 12",
                            "100-130K",
                            "+80-120K",
                            "180-250K",
                            "82%",
                        ],
                    ]}
                />
                <Note>
                    These are operational expectations, not stretch numbers.
                    Production infrastructure already in place. Cadence
                    proven. Editorial relationship live. The math compounds
                    if the reps don&apos;t break.
                </Note>

                <H2>The Primary Target — Insight Timer Alone</H2>
                <p style={paraStyle()}>
                    By Month 3, Insight Timer ALONE generates{" "}
                    <strong style={{ color: GOLD }}>
                        AED 30-40K/month net
                    </strong>
                    . By Month 12, IT alone hits AED 100-130K/month. This
                    is the manifest. The 4-engine stack delivers it:
                </p>
                <Table
                    headers={["IT Sub-Engine", "Month 3", "Month 12"]}
                    rows={[
                        [
                            "IT royalties · catalog plays · premium tier",
                            "20-28K",
                            "55-75K",
                        ],
                        [
                            "IT native course store (Courses #1, #2, #3)",
                            "5-7K",
                            "25-35K",
                        ],
                        [
                            "Featured-spot bonuses · editorial promo",
                            "3-5K",
                            "12-15K",
                        ],
                        [
                            "IT Live sessions (weekly Thursdays)",
                            "2-3K",
                            "8-12K",
                        ],
                        ["TOTAL FROM IT ALONE", "30-43K", "100-137K"],
                    ]}
                />

                <H2>Bonus Cream On Top</H2>
                <p style={paraStyle()}>
                    Running parallel to IT. These don&apos;t need to hit
                    their targets for the IT manifest to land. They&apos;re
                    pure upside, fully owned, customer data in your hands.
                </p>
                <Table
                    headers={["Bonus Stream", "Month 3", "Month 12"]}
                    rows={[
                        [
                            "Sleep Visual Sound Bath ($19 → $29)",
                            "+2-4K",
                            "+15-25K",
                        ],
                        [
                            "Online Paid Zoom Lives ($30, bi-weekly → weekly)",
                            "+6-10K",
                            "+25-40K",
                        ],
                        [
                            "Email list nurture sales (broadcast + sequences)",
                            "+2-4K",
                            "+20-35K",
                        ],
                        [
                            "/library Stripe products (Courses #2, #3 off-IT)",
                            "+0",
                            "+20-30K",
                        ],
                        ["TOTAL BONUS", "+10-18K", "+80-130K"],
                    ]}
                />
                <Note>
                    Combined Year-End ceiling: AED 180-250K/month. That
                    number is Veer&apos;s vision turned into operations.
                    Indefinitely scalable. Globally distributed. Time for
                    family preserved.
                </Note>

                <H2>The 4 Phase Roadmap (Months 1-12)</H2>

                <H3>Phase 1 · Foundation · Months 1-3</H3>
                <Checklist
                    items={[
                        "4 tracks/day Mon-Sat → 300+ catalog by Month 3",
                        "Weekly Thursday IT Lives (algorithm signal)",
                        "Course #1 launched on IT (June 20) + /course-1 Gumroad fallback",
                        "Sleep Visual Sound Bath shot + on Gumroad/Stripe",
                        "Bi-weekly Online Zoom Lives via Calendly + Stripe Connect",
                        "Email list 0 → 1,500-2,000 (Meta ads + organic)",
                        "Reply to Keigh — themes ask, format alignment",
                        "VA hire decision LOCKED (already in place — production capacity ready)",
                        "Build: /course-1, /library, /live, footer email capture, Meta Pixel",
                    ]}
                />

                <H3>Phase 2 · Compound · Months 4-6</H3>
                <Checklist
                    items={[
                        "Catalog crosses 500+ tracks · featured rotation steady state",
                        "Course #2 pre-launch (August) → launches early Phase 2",
                        "Email list 2,000 → 5,000 (Halbert weekly broadcasts firing)",
                        "Online Zoom Lives scale to weekly (15 → 25 seats)",
                        "Sleep Bath converts on retargeting (Meta ASC+)",
                        "Build: /course-2-prelaunch, /admin dashboard, referral system",
                        "MailerLite API migration (list crossed 200, automation flows on)",
                        "Behavioural email segmentation (clickers vs non-clickers branch)",
                    ]}
                />

                <H3>Phase 3 · Scale · Months 7-9</H3>
                <Checklist
                    items={[
                        "Catalog 700+ tracks · multiple Featured Teacher placements",
                        "Course #3 (signature Reset Method) launches October",
                        "Email list 5,000 → 12,000 (ad spend scaled, ASC+ optimised)",
                        "Weekly Zoom Lives at 25-40 seats consistently",
                        "Add quarterly intensive group programs ($499-999 USD tier)",
                        "Build: tiered course bundles, members area, retention loops",
                        "Hire community manager / second VA (Month 8-9)",
                        "Start documenting brand IP (book/manifesto outline?)",
                    ]}
                />

                <H3>Phase 4 · Empire · Months 10-12</H3>
                <Checklist
                    items={[
                        "Catalog 1,000+ tracks · IT relationship at editorial partner level",
                        "3 evergreen courses in IT native + /library Stripe",
                        "Email list 12,000+ · automated funnel runs without manual touch",
                        "Weekly Zoom Lives · waiting list forming · premium tier launches",
                        "Brand licensing conversations begin (hotels, spas, wellness retreats)",
                        "B2B distribution explored (corporate wellness platforms · NOT door-to-door)",
                        "Year-end income running at AED 180-250K/mo combined · IT alone at 100-130K",
                        "Plan year 2: international expansion, app, possible exit conversations",
                    ]}
                />

                <H2>The Weekly Rhythm</H2>
                <Table
                    headers={["Block", "Activity", "Output"]}
                    rows={[
                        [
                            "Morning · 3h",
                            "Recording session",
                            "4 tracks/day for IT (Mon-Sat)",
                        ],
                        [
                            "Midday · 2h",
                            "Marketing block",
                            "Emails, ads review, content prep",
                        ],
                        [
                            "Afternoon · 2h",
                            "Engagement block",
                            "IT replies, email replies, IG/social",
                        ],
                        [
                            "Evening · 1h",
                            "Strategic block",
                            "Course work, IT Live prep, planning",
                        ],
                        [
                            "Sunday",
                            "OFF — completely",
                            "Family + recovery (signal stays clean)",
                        ],
                    ]}
                />
                <Note>
                    ~8 hours/day Mon-Sat. NOT 12. Burnout is the #1 risk to
                    the 12-month number. VA hire = Month 3 budget line item.
                </Note>

                <H2>Week 1 — Days 0-7 — Target: AED 2-4K</H2>

                <H3>Day 1 (Tomorrow) — Reactivation Launch</H3>
                <Checklist
                    items={[
                        "Morning: Record + upload 4 tracks to IT",
                        "Midday: Send Email #1 to 90-list — Subject: 'Are you still up at 2 AM?'",
                        "Afternoon: Reply to anyone who replies (1:1 = highest conversion)",
                        "Afternoon: Outline Course #1 modules + lock price ($99 USD)",
                        "Evening: Plan tomorrow's 4 tracks (themes, frequencies, lengths)",
                    ]}
                />

                <H3>Day 2 — Production + IG Engagement</H3>
                <Checklist
                    items={[
                        "Morning: Record + upload 4 tracks",
                        "Midday: Monitor Email #1 metrics (target 30%+ open rate)",
                        "Midday: Review Meta Ad B (Tier-1 /sleep). Check CPL/CTR. DO NOT TWEAK.",
                        "Afternoon: Post on IG / IT — promote new tracks to existing followers",
                        "Evening: Begin Sleep Visual Sound Bath shoot setup",
                    ]}
                />

                <H3>Day 3 — Send Email #2</H3>
                <Checklist
                    items={[
                        "Morning: Record + upload 4 tracks",
                        "Midday: Send Email #2 — Subject: 'The one frequency I record at 4 AM'",
                        "Afternoon: Engage with replies + IT comments",
                        "Afternoon: Thursday IT Live prep (theme, intro, music selection)",
                        "Evening: Shoot 30-60 min of Sleep Sound Bath",
                    ]}
                />

                <H3>Day 4 — IT Live Day (Thursday)</H3>
                <Checklist
                    items={[
                        "Morning (lighter): Record 2-3 tracks",
                        "Midday: Promote tonight's Live on IG + WhatsApp Status",
                        "Midday: Email teaser to 90-list: 'Tonight, 8 PM GST — joining me?'",
                        "Afternoon: Final IT Live setup (audio check, room prep)",
                        "Evening 7-9 PM GST: GO LIVE — 30-45 min frequency work",
                        "Evening: Soft pitch paid Zoom slot in last 5 min of Live",
                        "Post-Live: Thank-you message to attendees",
                    ]}
                />

                <H3>Day 5 — Send Email #3 (Hard Close)</H3>
                <Checklist
                    items={[
                        "Morning: Record + upload 4 tracks",
                        "Midday: Send Email #3 — Subject: 'Last call: Thursday Zoom + Sleep Bath pre-order'",
                        "Afternoon: Reply to ALL replies personally",
                        "Afternoon: Push Sleep Sound Bath shoot to completion",
                        "Evening: Edit shot footage roughly (rough cut)",
                    ]}
                />

                <H3>Day 6 (Saturday) — Catch-Up + Plan</H3>
                <Checklist
                    items={[
                        "Morning: Record + upload 4 tracks",
                        "Midday: Review Week 1 metrics (opens, sales, uploads, followers)",
                        "Afternoon: Plan Week 2 (Course #1 pre-launch sequence)",
                        "Afternoon: Reply to Keigh's email (themes ask) if not already done",
                        "Evening: Family time — early dinner",
                    ]}
                />

                <H3>Day 7 (Sunday) — REST</H3>
                <Checklist
                    items={[
                        "❌ No production",
                        "❌ No emails sent",
                        "❌ No social posting",
                        "✅ Family",
                        "✅ Walk / meditation / your own track on loop",
                        "✅ Optional: journal Week 2 priorities",
                    ]}
                />

                <H2>3-Email Reactivation Sequence</H2>

                <H3>Email #1 — Day 1 — Story + Soft Pitch</H3>
                <Subject>Are you still up at 2 AM?</Subject>
                <Body>
                    Hey [First Name],{"\n\n"}I moved to Dubai 4 weeks ago.
                    {"\n\n"}Most days I&apos;m up at 2 AM mixing a track —
                    not because I have to, but because that&apos;s the only
                    window where the city is quiet enough for the frequencies
                    to land cleanly.{"\n\n"}If you&apos;ve been up at the
                    same hour, you know the feeling.{"\n\n"}I&apos;ve spent
                    13 years making sound for radio, films, and 40 million
                    streamed plays. None of it prepared me for what I now
                    make — frequency-based audio designed to do one thing:
                    reset a nervous system that won&apos;t switch off.
                    {"\n\n"}I&apos;ve just deployed 12 free tracks on
                    Insight Timer. Sleep, anxiety, recovery — pick what you
                    need.{"\n\n"}Here&apos;s the link:
                    https://www.insighttimer.com/resetsanctuary{"\n\n"}
                    I&apos;ll write you again in 2 days with the one
                    frequency I keep returning to at 4 AM.{"\n\n"}— Tatva K
                    {"\n\n"}P.S. If you&apos;re a paying subscriber on IT,
                    please play these on your premium feed too. It helps me
                    keep these free for you.
                </Body>

                <H3>Email #2 — Day 3 — Value Bomb</H3>
                <Subject>The one frequency I record at 4 AM</Subject>
                <Body>
                    Hey [First Name],{"\n\n"}There&apos;s a frequency called
                    174 Hz.{"\n\n"}It&apos;s not &ldquo;magic.&rdquo;
                    It&apos;s not &ldquo;ancient.&rdquo; It&apos;s just been
                    observed, across more than 40 small studies, to correlate
                    with reduced muscular tension and lower cortisol when
                    played at 60-90 dB during sleep onset.{"\n\n"}I record
                    at 174 Hz between 3-5 AM because that&apos;s when my own
                    nervous system is quietest enough to notice if a layer
                    of the mix is fighting against it.{"\n\n"}The track
                    below is my latest 174 Hz session.{"\n\n"}▶ Listen:
                    https://www.insighttimer.com/resetsanctuary{"\n\n"}
                    It&apos;s 90 minutes. Play it at 60% volume. Let it run
                    while you&apos;re working, falling asleep, or driving.
                    {"\n\n"}I&apos;m launching a full course around this
                    frequency stack (174 / 285 / 528 Hz) on June 20.
                    Early-bird $79. Reply with &ldquo;course&rdquo; if you
                    want first access.{"\n\n"}— Tatva K{"\n\n"}P.S. The
                    course locks at $99 from June 21. Reply now to lock in
                    $79.
                </Body>

                <H3>Email #3 — Day 7 — Hard Close</H3>
                <Subject>
                    Last call: Thursday Zoom + Sleep Bath pre-order
                </Subject>
                <Body>
                    Hey [First Name],{"\n\n"}Two quick things, then
                    I&apos;ll get out of your inbox:{"\n\n"}1) I&apos;m
                    running a paid Zoom session this Thursday, 8 PM GST. 90
                    minutes. Live frequency work. 15 seats. $30 USD. These
                    usually sell out within a few hours of announcement.
                    {"\n   "}👉 Lock your seat: [Calendly link]{"\n\n"}2)
                    I&apos;m pre-shooting a Sleep Visual Sound Bath this
                    week. 60-minute visual + audio journey, never released
                    elsewhere. Pre-order at $19. Locks at $29 after launch.
                    {"\n   "}👉 Pre-order: [Gumroad link]{"\n\n"}
                    That&apos;s it. Reply with any questions.{"\n\n"}—
                    Tatva K{"\n\n"}P.S. If neither is for you right now, no
                    worries. Just hit the Insight Timer link:
                    https://www.insighttimer.com/resetsanctuary
                </Body>

                <H2>Course #1 — Frequency Sleep Reset</H2>
                <p style={paraStyle()}>
                    <strong style={{ color: GOLD }}>Launch:</strong> June 20
                    on IT Native (preferred) or /course-1 Gumroad fallback.{" "}
                    <strong style={{ color: GOLD }}>Price:</strong> $99 USD
                    (~AED 365).{" "}
                    <strong style={{ color: GOLD }}>Format:</strong> 5 audio
                    modules + intro PDF + 6mo Q&A access (online only).
                </p>
                <Table
                    headers={["Module", "Content"]}
                    rows={[
                        [
                            "1 — The Science",
                            "30 min audio + 1-page PDF on frequency biology",
                        ],
                        [
                            "2 — 174 Hz Body Reset",
                            "60 min audio session",
                        ],
                        [
                            "3 — 285 Hz Cellular Recovery",
                            "60 min audio session",
                        ],
                        [
                            "4 — 528 Hz Heart-Brain Coherence",
                            "60 min audio session",
                        ],
                        [
                            "5 — 8-Hour Sleep Journey",
                            "Full overnight audio",
                        ],
                    ]}
                />

                <H2>Online Paid Zoom Lives (Bi-Weekly)</H2>
                <p style={paraStyle()}>
                    <strong style={{ color: GOLD }}>Format:</strong>{" "}
                    90-minute live frequency session via Zoom.{" "}
                    <strong style={{ color: GOLD }}>Capacity:</strong> 15
                    seats max (intimate).{" "}
                    <strong style={{ color: GOLD }}>Price:</strong> $30 USD
                    (~AED 110).{" "}
                    <strong style={{ color: GOLD }}>Frequency:</strong>{" "}
                    Bi-weekly Thursdays, 8 PM GST.
                </p>
                <p style={paraStyle()}>
                    Booked via Calendly. Paid via Stripe Connect. Zoom invite
                    auto-sent on payment.{" "}
                    <strong style={{ color: GOLD }}>
                        15 seats × $30 × 2 sessions/month = $900/month
                        (~AED 3,300)
                    </strong>{" "}
                    by Month 2. Scales to AED 6-10K by Month 3 if 2 sessions
                    fill consistently.
                </p>

                <H2>Sleep Visual Sound Bath ($19)</H2>
                <p style={paraStyle()}>
                    <strong style={{ color: GOLD }}>Format:</strong> 60-min
                    audio + visual journey, never released elsewhere.{" "}
                    <strong style={{ color: GOLD }}>Price:</strong> $19
                    pre-order → $29 after launch.{" "}
                    <strong style={{ color: GOLD }}>Delivery:</strong> Private
                    Vimeo link via email after Stripe payment confirms.{" "}
                    <strong style={{ color: GOLD }}>Platform:</strong> Gumroad
                    initially → migrate to /library Stripe by Month 2.
                </p>

                <H2>Weekly Metrics Dashboard (Track Saturdays)</H2>
                <Table
                    headers={["Metric", "Week 1", "Week 2", "Week 3", "Week 4"]}
                    rows={[
                        ["Tracks uploaded (/24)", "", "", "", ""],
                        ["IT followers gained", "", "", "", ""],
                        ["IT plays (week)", "", "", "", ""],
                        ["Featured tracks (running total)", "", "", "", ""],
                        ["Emails sent", "", "", "", ""],
                        ["Open rate %", "", "", "", ""],
                        ["Click rate %", "", "", "", ""],
                        ["New email subscribers", "", "", "", ""],
                        ["Total list size", "", "", "", ""],
                        ["Meta Ad B CPL (Tier-1)", "", "", "", ""],
                        ["IT Live attendance", "", "", "", ""],
                        ["Paid Zoom seats sold", "", "", "", ""],
                        ["Sleep Bath pre-orders", "", "", "", ""],
                        ["Course #1 pre-orders", "", "", "", ""],
                        ["TOTAL REVENUE (AED)", "", "", "", ""],
                    ]}
                />

                <H2>The &ldquo;Do Not Break&rdquo; List</H2>
                <ol style={olStyle()}>
                    <li>
                        Don&apos;t use the 26K MailerLite list for Reset
                        Sanctuary — ESP ban risk
                    </li>
                    <li>
                        Don&apos;t break the upload chain — 3 missed days =
                        catalog growth stalls
                    </li>
                    <li>
                        Don&apos;t skip 2 IT Lives in a row — algorithm
                        punishes
                    </li>
                    <li>
                        Don&apos;t send a sloppy email blast — one unsub
                        spike kills future deliverability
                    </li>
                    <li>
                        Don&apos;t touch ads during 7-14 day learning phase
                    </li>
                    <li>
                        Don&apos;t add manual retargeting/Lookalike on top
                        of ASC+ — Andromeda handles internally
                    </li>
                    <li>
                        Don&apos;t expect Course #1 to hit AED 15K — it&apos;s
                        a learning launch (4-10K)
                    </li>
                    <li>
                        Don&apos;t burn out in Month 4 — VA hire = Month 3
                        line item
                    </li>
                    <li>
                        Don&apos;t work Sundays — signal stays clean
                    </li>
                    <li>
                        Don&apos;t mix in-person streams into the online
                        plan — they&apos;re separate engines, separate math
                    </li>
                </ol>

                <H2>Revenue Waterfall — Year At Large</H2>
                <Table
                    headers={[
                        "Month",
                        "IT Alone (AED)",
                        "+ Bonus",
                        "Combined (AED)",
                    ]}
                    rows={[
                        ["Month 1", "4-7K", "+2-3K", "6-10K"],
                        ["Month 2", "14-22K", "+5-9K", "19-31K"],
                        ["Month 3", "30-40K ✓", "+10-18K", "40-58K"],
                        ["Month 6", "45-60K", "+15-25K", "60-85K"],
                        ["Month 9", "70-90K", "+35-50K", "105-140K"],
                        ["Month 12", "100-130K", "+80-120K", "180-250K"],
                    ]}
                />
                <Note>
                    Month 3 IT-alone target is the manifest. Speak it.
                    Print it. Look at it daily. Year 12 is the empire.
                </Note>

                <H2>Meta Ad Strategy (Online Only)</H2>
                <Table
                    headers={["Ad", "Goal", "Budget", "Notes"]}
                    rows={[
                        [
                            "Ad B — Tier-1 /sleep",
                            "Email captures → IT follow",
                            "INR 1,500/day",
                            "UK/US/AUS/CAN/EU geo · ~70 emails/day",
                        ],
                        [
                            "Ad C — Purchase ASC+",
                            "Sleep Bath / Course #1 sales",
                            "INR 1,500/day (Day 3+)",
                            "Broad audience · Andromeda handles segmentation",
                        ],
                    ]}
                />
                <Note>
                    No Ad A. No HNWI in-person Lead Gen. Online only.
                </Note>

                <H2>What to Ask EM (Build Commands)</H2>
                <p style={paraStyle()}>
                    Drop ONE command per chat. Minimum credits, maximum
                    velocity.
                </p>
                <Table
                    headers={["Command", "What EM Ships"]}
                    rows={[
                        [
                            "build course-1",
                            "/course-1 fallback Gumroad page (if IT delays)",
                        ],
                        [
                            "build library",
                            "/library Stripe checkout for Sleep Bath",
                        ],
                        [
                            "build live",
                            "/live Calendly + Stripe Connect ($30 USD bi-weekly Zoom)",
                        ],
                        ["build referral", "Referral system in /sleep"],
                        [
                            "build admin",
                            "Password-protected admin dashboard",
                        ],
                        [
                            "migrate mailerlite",
                            "API automation when list hits 200",
                        ],
                        [
                            "build course-2-prelaunch",
                            "/course-2 pre-launch page (August)",
                        ],
                    ]}
                />

                <H2>Decide This Week</H2>
                <Checklist
                    items={[
                        "Course #1 final topic (suggest: Frequency Sleep Reset)",
                        "Course #1 final price ($99 USD recommended)",
                        "Sleep Visual Sound Bath shoot date (this week or next)",
                        "Reply to Keigh's email (themes ask)",
                        "First Calendly setup for bi-weekly Zoom (theme + first date)",
                    ]}
                />

                <footer
                    style={{
                        marginTop: 80,
                        paddingTop: 32,
                        borderTop: `1px solid rgba(201,162,39,0.18)`,
                        textAlign: "center",
                    }}
                >
                    <p
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 18,
                            lineHeight: 1.5,
                            margin: "0 0 16px",
                            fontStyle: "italic",
                            fontWeight: 400,
                        }}
                    >
                        Hand-produced. Hand-mixed. Human-made. No AI.
                    </p>
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 10,
                            letterSpacing: "0.4em",
                            textTransform: "uppercase",
                            margin: 0,
                            fontWeight: 600,
                        }}
                    >
                        The Reset Sanctuary · Pure Online · UAE
                    </p>
                </footer>
            </article>

            <style>{`
                @media print {
                    .plan-screen-only { display: none !important; }
                    body, .App, .plan-page {
                        background-color: #0a0a0a !important;
                        color: #e8e8e8 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    [data-testid="site-footer"],
                    [data-testid="floating-whatsapp"],
                    nav { display: none !important; }
                    article {
                        max-width: 100% !important;
                        border: none !important;
                        padding: 0 !important;
                    }
                    @page { size: A4; margin: 18mm 14mm; }
                }
            `}</style>
        </main>
    );
};

const H2 = ({ children }) => (
    <h2
        className="font-serif"
        style={{
            color: "#fff",
            fontSize: 26,
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            margin: "56px 0 22px",
            paddingBottom: 12,
            borderBottom: `1px solid rgba(201,162,39,0.25)`,
        }}
    >
        {children}
    </h2>
);
const H3 = ({ children }) => (
    <h3
        style={{
            color: GOLD,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            margin: "32px 0 14px",
        }}
    >
        {children}
    </h3>
);
const paraStyle = () => ({
    color: "#cfcfcf",
    fontSize: 14,
    lineHeight: 1.75,
    margin: "0 0 16px",
});
const olStyle = () => ({
    color: "#cfcfcf",
    fontSize: 14,
    lineHeight: 1.8,
    paddingLeft: 22,
    margin: "0 0 16px",
});
const blockBox = () => ({
    border: `1px solid rgba(201,162,39,0.3)`,
    padding: "28px 32px",
    margin: "0 0 48px",
    backgroundColor: "rgba(201,162,39,0.04)",
});
const quoteStyle = () => ({
    color: "#fff",
    fontSize: 16,
    lineHeight: 1.75,
    fontStyle: "italic",
    margin: "0 0 12px",
    fontFamily: "Playfair Display, serif",
});
const attribStyle = () => ({
    color: GOLD,
    fontSize: 11,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    margin: 0,
    fontWeight: 600,
    textAlign: "right",
});
const Note = ({ children }) => (
    <p
        style={{
            color: GOLD,
            fontSize: 12,
            lineHeight: 1.7,
            margin: "0 0 24px",
            fontStyle: "italic",
            letterSpacing: "0.02em",
        }}
    >
        {children}
    </p>
);
const Checklist = ({ items }) => (
    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
        {items.map((item, i) => (
            <li
                key={i}
                style={{
                    color: "#cfcfcf",
                    fontSize: 13.5,
                    lineHeight: 1.75,
                    paddingLeft: 28,
                    position: "relative",
                    marginBottom: 8,
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 5,
                        width: 14,
                        height: 14,
                        border: `1.5px solid ${GOLD}`,
                        display: "inline-block",
                    }}
                />
                {item}
            </li>
        ))}
    </ul>
);
const Subject = ({ children }) => (
    <p
        style={{
            color: GOLD,
            fontSize: 13,
            letterSpacing: "0.05em",
            margin: "0 0 12px",
            fontWeight: 500,
            fontStyle: "italic",
        }}
    >
        Subject: <span style={{ color: "#fff" }}>{children}</span>
    </p>
);
const Body = ({ children }) => (
    <pre
        style={{
            color: "#cfcfcf",
            fontSize: 12.5,
            lineHeight: 1.75,
            fontFamily: "Inter, sans-serif",
            whiteSpace: "pre-wrap",
            backgroundColor: "rgba(201,162,39,0.03)",
            border: `1px solid rgba(201,162,39,0.15)`,
            padding: "20px 22px",
            margin: "0 0 24px",
        }}
    >
        {children}
    </pre>
);
const Table = ({ headers, rows }) => (
    <div
        style={{
            overflowX: "auto",
            margin: "0 0 28px",
            border: `1px solid rgba(201,162,39,0.2)`,
        }}
    >
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12.5,
            }}
        >
            <thead>
                <tr style={{ backgroundColor: "rgba(201,162,39,0.08)" }}>
                    {headers.map((h, i) => (
                        <th
                            key={i}
                            style={{
                                color: GOLD,
                                fontWeight: 600,
                                fontSize: 11,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                padding: "12px 14px",
                                textAlign: "left",
                                borderBottom: `1px solid rgba(201,162,39,0.2)`,
                            }}
                        >
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, ri) => (
                    <tr
                        key={ri}
                        style={{
                            borderBottom: `1px solid rgba(201,162,39,0.1)`,
                        }}
                    >
                        {row.map((cell, ci) => {
                            const isTotal =
                                typeof cell === "string" &&
                                cell.toString().startsWith("TOTAL");
                            return (
                                <td
                                    key={ci}
                                    style={{
                                        color: isTotal ? GOLD : "#cfcfcf",
                                        fontSize: 12.5,
                                        lineHeight: 1.6,
                                        padding: "10px 14px",
                                        fontWeight: isTotal ? 600 : 400,
                                    }}
                                >
                                    {cell}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default Plan;
